import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { MetricCard } from "@/components/metrics/MetricCard";
import { PortfolioCard } from "@/components/portfolio/PortfolioCard";
import { getProject, getRelatedProjects, type PortfolioProject } from "@/content/portfolio";
import { getCaseStudy } from "@/content/caseStudies";
import { getPortfolioNarrative } from "@/content/portfolioNarratives";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { Picture } from "@/components/media/Picture";
import { siteConfig } from "@/config/site";


export const Route = createFileRoute("/portfolio/$slug")({
  loader: ({ params }): { project: PortfolioProject } => {
    const project = getProject(params.slug);
    if (!project) throw notFound();
    return { project };
  },

  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: `Project not found — ${siteConfig.name}` },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const p = loaderData.project;
    const path = `/portfolio/${params.slug}/`;
    const title = `${p.client} — ${p.title}`;
    const base = pageMeta({
      path,
      title,
      description: p.tagline,
      image: p.cover,
      type: "article",
      keywords: [
        p.client,
        p.industry,
        `${p.industry} case study`,
        `${p.industry} SEO ${siteConfig.contact.address.city}`,
        `${siteConfig.contact.address.city} SEO agency portfolio`,

        `SEO results ${siteConfig.contact.address.state}`,
        siteConfig.name,

      ],
    });
    const narrative = getPortfolioNarrative(params.slug);
    return {
      ...base,
      scripts: ldScripts(
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Portfolio", path: "/portfolio" },
          { name: p.client, path },
        ]),
        ...(narrative?.faqs?.length ? [faqLd(narrative.faqs)] : []),
      ),
    };
  },
  component: PortfolioDetail,
  notFoundComponent: () => (
    <div className="min-h-dvh bg-background">
      <SiteNav />
      <main id="main" tabIndex={-1} className="container-page py-32 text-center">
        <h1 className="font-display text-4xl">Project not found</h1>
        <Link to="/portfolio/" className="mt-6 inline-block text-primary">
          ← Back to portfolio
        </Link>
      </main>
    </div>
  ),
});

function PortfolioDetail() {
  const { project } = Route.useLoaderData() as { project: PortfolioProject };
  const caseStudy = getCaseStudy(project.slug);
  const related = getRelatedProjects(project.slug);
  const narrative = getPortfolioNarrative(project.slug);
  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-16 md:pt-24">
          <Link to="/portfolio/" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary">
            ← Portfolio
          </Link>
          <div className="mt-6 grid gap-10 lg:grid-cols-[1.15fr_1fr] items-start">
            <div>
              <div className="text-xs uppercase tracking-widest text-primary">
                {project.industry} · {project.window}
              </div>
              <h1 className="mt-3 font-display text-4xl md:text-6xl leading-[1.02]">
                {project.client}
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-xl">
                {project.tagline}
              </p>
              <dl className="mt-10 grid grid-cols-2 gap-6 text-sm">
                {[
                  ["Industry", project.industry],
                  ["Region", project.country],
                  ["Platform", project.platform],
                  ["Duration", project.duration],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[0.7rem] uppercase tracking-widest text-muted-foreground">
                      {k}
                    </dt>
                    <dd className="mt-1 font-medium text-foreground">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-8 flex flex-wrap gap-2">
                {project.services.map((s) => (
                  <span
                    key={s}
                    className="rounded-full border border-border px-3 py-1 text-xs"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div className="surface-card overflow-hidden">
              <Picture
                variantKey={project.coverVariant}
                alt={`${project.client} results`}
                sizes="(min-width: 1024px) 50vw, 100vw"
                priority
                imgClassName="w-full h-auto object-cover"
              />
            </div>
          </div>
        </section>

        <section className="container-page py-20">
          <h2 className="font-display text-2xl md:text-3xl">Measured results</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Sourced from {[...new Set(project.metrics.map((m) => m.source).filter(Boolean))].join(", ")}.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {project.metrics.map((m) => (
              <MetricCard key={m.label} metric={m} />
            ))}
          </div>
        </section>

        <section className="container-page py-10">
          <div className="grid gap-14 lg:grid-cols-3">
            <Block title="Goals" items={project.goals} />
            <Block title="Challenges" items={project.challenges} />
            <Block title="What we did" items={project.solutions} />
          </div>
        </section>

        <section className="container-page py-10">
          <div className="grid gap-14 md:grid-cols-2">
            <Block title="Deliverables" items={project.deliverables} />
            <Block title="Tools & stack" items={project.stack} />
          </div>
        </section>

        {narrative && (
          <section className="container-page py-16">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
                Inside the engagement
              </h2>
              <div className="mt-6 space-y-5">
                {narrative.summary.map((p, i) => (
                  <p key={i} className="text-[1.05rem] leading-relaxed text-foreground/85">{p}</p>
                ))}
              </div>

              {narrative.sections.map((s, i) => (
                <div key={i} className="mt-14">
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight">{s.heading}</h3>
                  <div className="mt-5 space-y-5">
                    {s.body.map((p, j) => (
                      <p key={j} className="text-[1.05rem] leading-relaxed text-foreground/85">{p}</p>
                    ))}
                  </div>
                  {s.bullets && s.bullets.length > 0 && (
                    <ul className="mt-6 space-y-3">
                      {s.bullets.map((b, j) => (
                        <li key={j} className="flex gap-3 text-[1.02rem] leading-relaxed text-foreground/85">
                          <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {narrative.faqs.length > 0 && (
                <div className="mt-16">
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight">
                    Frequently asked questions
                  </h3>
                  <dl className="mt-6 space-y-6">
                    {narrative.faqs.map((f, i) => (
                      <div key={i} className="rounded-2xl border border-border p-6">
                        <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                        <dd className="mt-2 text-foreground/85 leading-relaxed">{f.a}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          </section>
        )}


        {caseStudy && (
          <section className="container-page py-20">
            <div className="surface-card p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6 justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-primary">
                  Long-form
                </div>
                <h3 className="mt-2 font-display text-2xl md:text-3xl">
                  Read the full case study
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {caseStudy.readingTime} · {caseStudy.hero.deck}
                </p>
              </div>
              <Link
                to="/case-studies/$slug/"
                params={{ slug: caseStudy.slug }}
                className="inline-flex whitespace-nowrap rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                Read the story →
              </Link>
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="container-page py-20">
            <h2 className="font-display text-2xl md:text-3xl">Related work</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((p) => (
                <PortfolioCard key={p.slug} project={p} />
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function Block({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-widest text-primary">{title}</h3>
      <ul className="mt-4 space-y-3">
        {items.map((it) => (
          <li key={it} className="flex gap-3 text-sm text-foreground/90">
            <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
            <span className="leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
